/**
 * Using for recomendation products and recent view products
 */
if (!customElements.get('product-slider')) {
  customElements.define(
    'product-slider',
    class ProductSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          productsWrap: '.products-grid-wrap',
          products: '.products-grid',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.elements = FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.elements.section = this.closest(`.section-${this.sectionId}`);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items);
        this.tabletItems = parseInt(this.dataset.tabletItems);
        this.totalItems = parseInt(this.dataset.totalItems);
        this.paginationType = this.dataset.paginationType || 'bullets';

        this.sliderInstance = false;

        // Wire the mobile arrows first — they drive the native scroll-snap row and must work
        // even when the desktop Swiper is disabled (mobile keeps native scrolling). Mirrors
        // the landing-page featured-collection slider so the PDP carousels match on mobile.
        this.initSwipeNav();

        if (!this.enableSlider) return;

        if (!this.elements.productsWrap || !this.elements.products) return;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);

        const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
        mqlTablet.onchange = this.init.bind(this);

        this.init();
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          const currentItems = FoxTheme.config.mqlTablet ? this.tabletItems : this.items;

          if (this.totalItems > currentItems) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        const columnGap = window.getComputedStyle(this.elements.products).getPropertyValue('--f-column-gap');
        const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;

        this.sliderOptions = {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: spaceBetween,
          // Advance a whole "page" of visible cards per arrow click instead of one card at a
          // time — mirrors the landing-page featured-collection slider so the PDP recommendation
          // carousels feel identical. slidesPerGroup tracks slidesPerView at every breakpoint.
          speed: 500,
          navigation: {
            nextEl: this.elements.nextEl,
            prevEl: this.elements.prevEl,
          },
          pagination: {
            el: this.elements.pagination,
            type: this.paginationType,
          },
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems,
              slidesPerGroup: this.tabletItems,
            },
            1024: {
              slidesPerView: this.items,
              slidesPerGroup: this.items,
            },
          },
          loop: false,
          threshold: 2,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
        };

        this.elements.productsWrap.classList.add(this.classes.swiper);
        this.elements.products.classList.remove(this.classes.grid);
        this.elements.products.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this.elements.productsWrap, this.sliderOptions, [
          FoxTheme.Swiper.Mousewheel,
        ]);
        this.sliderInstance.init();
        this.handleAccessibility();
        this.fixQuickviewDuplicate();
        this.calcNavButtonsPosition();
      }

      handleAccessibility() {
        const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        focusableElements.forEach((element) => {
          element.addEventListener('focusin', (event) => {
            if (event.relatedTarget !== null) {
              if (element.closest('.swiper-slide')) {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
              }
            } else {
              element.blur();
            }
          });
        });
      }

      destroySlider() {
        this.elements.productsWrap.classList.remove(this.classes.swiper);
        this.elements.products.classList.remove(this.classes.swiperWrapper);
        this.elements.products.classList.add(this.classes.grid);
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      calcNavButtonsPosition() {
        if (this.dataset.navigationPosition !== 'center_image') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        const controls = this.querySelector('.swiper-controls');
        if (controls && firstMedia && firstMedia.clientHeight > 0) {
          controls.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
        }
      }

      // Mobile-only arrow controls. On mobile the Swiper instance is destroyed and the row
      // scrolls via native CSS scroll-snap on `.products-grid-wrap`, so these buttons drive
      // scrollBy() on that scroller rather than the Swiper API. Mirrors featured-collection.js.
      initSwipeNav() {
        this.swipeScroller = this.elements.productsWrap;
        this.swipeNavPrev = this.querySelector('[data-swipe-prev]');
        this.swipeNavNext = this.querySelector('[data-swipe-next]');
        if (!this.swipeScroller || !this.swipeNavPrev || !this.swipeNavNext) return;

        this.swipeNavPrev.addEventListener('click', () => this.swipeByPage(-1));
        this.swipeNavNext.addEventListener('click', () => this.swipeByPage(1));

        const sync = () => requestAnimationFrame(() => this.updateSwipeNavState());
        this.swipeScroller.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);
        sync();
      }

      swipeByPage(direction) {
        const scroller = this.swipeScroller;
        const inner = this.elements.products;
        const firstSlide = inner && inner.children[0];
        if (!scroller || !firstSlide) return;
        const gap = parseFloat(getComputedStyle(inner).columnGap) || 0;
        const step = firstSlide.getBoundingClientRect().width + gap;
        if (step <= 0) return;
        // Move a full viewport of cards per tap so each click clearly reveals more.
        const perPage = Math.max(1, Math.round(scroller.clientWidth / step));
        scroller.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
      }

      updateSwipeNavState() {
        if (!this.swipeScroller || !this.swipeNavPrev || !this.swipeNavNext) return;
        const maxScroll = this.swipeScroller.scrollWidth - this.swipeScroller.clientWidth;
        const scrolled = Math.abs(this.swipeScroller.scrollLeft); // abs() keeps it correct under RTL
        this.swipeNavPrev.disabled = scrolled <= 1;
        this.swipeNavNext.disabled = scrolled >= maxScroll - 1;
      }
    }
  );
}
